import axios from "axios";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Swal from "sweetalert2";

const API = "/api/auditoria/tipo-documento";

const authConfig = () => ({
  headers: {
    Authorization: `Bearer ${localStorage.getItem("token") || ""}`,
  },
});

const ESTADO_INICIAL = {
  nombreTipoDocumento: "",
  codigoTipoDocumento: "",
  descripcionTipoDocumento: "",
  moduloDocumento: "",
  modeloDocumento: "",
  permiteAuditoria: true,
  estadoTipoDocumento: "Activo",
  observacionTipoDocumento: "",
};

const Auditoria_RegistrarTipoDocumento = () => {
  const navigate = useNavigate();
  const { id } = useParams();

  const editando = useMemo(() => Boolean(id), [id]);

  const [form, setForm] = useState(ESTADO_INICIAL);
  const [loading, setLoading] = useState(false);
  const [cargando, setCargando] = useState(false);

  const manejarError = useCallback(
    (error, mensaje) => {
      if (error?.response?.status === 401) {
        localStorage.removeItem("token");
        Swal.fire("Sesión expirada", "Inicia sesión nuevamente.", "warning").then(
          () => navigate("/login")
        );
        return;
      }

      Swal.fire(
        "Error",
        error?.response?.data?.message || mensaje || "Ocurrió un error.",
        "error"
      );
    },
    [navigate]
  );

  const cargar = useCallback(async () => {
    if (!editando) return;

    try {
      setCargando(true);
      const { data } = await axios.get(`${API}/${id}`, authConfig());

      const item = data?.tipoDocumento;

      if (!item) throw new Error("Tipo de documento no encontrado.");

      setForm({
        nombreTipoDocumento: item.nombreTipoDocumento || "",
        codigoTipoDocumento: item.codigoTipoDocumento || "",
        descripcionTipoDocumento: item.descripcionTipoDocumento || "",
        moduloDocumento: item.moduloDocumento || "",
        modeloDocumento: item.modeloDocumento || "",
        permiteAuditoria: item.permiteAuditoria !== false,
        estadoTipoDocumento: item.estadoTipoDocumento || "Activo",
        observacionTipoDocumento: item.observacionTipoDocumento || "",
      });
    } catch (error) {
      manejarError(error, "No se pudo cargar el registro.");
    } finally {
      setCargando(false);
    }
  }, [editando, id, manejarError]);

  useEffect(() => {
    cargar();
  }, [cargar]);

  const cambiar = (campo, valor) => {
    setForm((prev) => ({
      ...prev,
      [campo]: valor,
    }));
  };

  const guardar = async (e) => {
    e.preventDefault();

    if (
      !form.nombreTipoDocumento.trim() ||
      !form.codigoTipoDocumento.trim() ||
      !form.modeloDocumento.trim()
    ) {
      Swal.fire(
        "Atención",
        "Nombre, código y modelo MongoDB son obligatorios.",
        "warning"
      );
      return;
    }

    try {
      setLoading(true);

      if (editando) {
        await axios.put(`${API}/${id}`, form, authConfig());
      } else {
        await axios.post(API, form, authConfig());
      }

      await Swal.fire(
        "Correcto",
        editando
          ? "Tipo de documento actualizado."
          : "Tipo de documento registrado.",
        "success"
      );

      navigate("/auditoria/tipo-documento");
    } catch (error) {
      manejarError(error, "No se pudo guardar el tipo de documento.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container py-4">
      <div className="card shadow-sm">
        <div className="card-header">
          <h4 className="mb-0">
            {editando
              ? "Actualizar tipo de documento"
              : "Registrar tipo de documento"}
          </h4>
        </div>

        <form onSubmit={guardar}>
          <div className="card-body">
            {cargando ? (
              <div className="text-center py-4">Cargando...</div>
            ) : (
              <div className="row g-3">
                <div className="col-md-6">
                  <label className="form-label">Nombre *</label>
                  <input
                    className="form-control"
                    value={form.nombreTipoDocumento}
                    onChange={(e) =>
                      cambiar("nombreTipoDocumento", e.target.value)
                    }
                    required
                  />
                </div>

                <div className="col-md-3">
                  <label className="form-label">Código *</label>
                  <input
                    className="form-control"
                    value={form.codigoTipoDocumento}
                    onChange={(e) =>
                      cambiar(
                        "codigoTipoDocumento",
                        e.target.value.toUpperCase().replace(/\s+/g, "_")
                      )
                    }
                    required
                  />
                </div>

                <div className="col-md-3">
                  <label className="form-label">Estado</label>
                  <select
                    className="form-select"
                    value={form.estadoTipoDocumento}
                    onChange={(e) =>
                      cambiar("estadoTipoDocumento", e.target.value)
                    }
                  >
                    <option value="Activo">Activo</option>
                    <option value="Inactivo">Inactivo</option>
                  </select>
                </div>

                <div className="col-md-6">
                  <label className="form-label">Módulo documental</label>
                  <input
                    className="form-control"
                    value={form.moduloDocumento}
                    onChange={(e) => cambiar("moduloDocumento", e.target.value)}
                    placeholder="Ej. Etapa práctica"
                  />
                </div>

                <div className="col-md-6">
                  <label className="form-label">
                    Nombre exacto del modelo MongoDB *
                  </label>
                  <input
                    className="form-control font-monospace"
                    value={form.modeloDocumento}
                    onChange={(e) => cambiar("modeloDocumento", e.target.value)}
                    placeholder="Ej. Sena_Bitacoras"
                    required
                  />
                  <small className="text-muted">
                    Debe coincidir con el nombre usado en model('...', schema).
                  </small>
                </div>

                <div className="col-12">
                  <label className="form-label">Descripción</label>
                  <textarea
                    className="form-control"
                    rows="3"
                    value={form.descripcionTipoDocumento}
                    onChange={(e) =>
                      cambiar("descripcionTipoDocumento", e.target.value)
                    }
                  />
                </div>

                <div className="col-md-4">
                  <div className="form-check mt-4">
                    <input
                      id="permiteAuditoria"
                      type="checkbox"
                      className="form-check-input"
                      checked={form.permiteAuditoria}
                      onChange={(e) =>
                        cambiar("permiteAuditoria", e.target.checked)
                      }
                    />
                    <label
                      htmlFor="permiteAuditoria"
                      className="form-check-label"
                    >
                      Permitir auditoría
                    </label>
                  </div>
                </div>

                <div className="col-12">
                  <label className="form-label">Observación</label>
                  <textarea
                    className="form-control"
                    rows="3"
                    value={form.observacionTipoDocumento}
                    onChange={(e) =>
                      cambiar("observacionTipoDocumento", e.target.value)
                    }
                  />
                </div>
              </div>
            )}
          </div>

          <div className="card-footer d-flex justify-content-end gap-2">
            <button
              type="button"
              className="btn btn-outline-secondary"
              onClick={() => navigate("/auditoria/tipo-documento")}
              disabled={loading}
            >
              Cancelar
            </button>

            <button
              type="submit"
              className="btn btn-primary"
              disabled={loading || cargando}
            >
              {loading ? "Guardando..." : "Guardar"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Auditoria_RegistrarTipoDocumento;
